import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { Button } from 'ustore-ui';

import { ResetPasswordService } from '../../core/services/reset-password.service';
import { ToastService } from '../../shared/services/toast.service';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { PasswordRequirementsComponent, PasswordRequirements } from '../../shared/components/password-requirements/password-requirements.component';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { UtmPreservationService } from '../../core/services/utm-preservation.service';

@Component({
  selector: 'umkt-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    Button,
    RouterModule,
    FooterComponent,
    AuthLayoutComponent,
    PasswordRequirementsComponent,
    CustomInputComponent,
    I18nPipe
  ],
  templateUrl: './reset-password.component.html',
  styleUrls: ['../../../assets/style/login-register.component.scss']
})
export class ResetPasswordComponent implements OnInit {
  @ViewChild('passwordInput') passwordInput!: CustomInputComponent;
  @ViewChild('confirmPasswordInput') confirmPasswordInput!: CustomInputComponent;

  resetForm!: FormGroup;
  
  loadingToken: boolean = false;  // Para validação do token
  loadingSubmit: boolean = false; // Para envio da senha
  
  error: string = '';
  token: string = '';
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  // controle de estado pristine da senha
  isPasswordPristine = true;

  // Requisitos de senha em tempo real
  passwordRequirements: PasswordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasSpecialChar: false,
    hasNumber: false
  };

  private isProcessingToken = false;
  private fb = inject(FormBuilder);
  private resetPasswordService = inject(ResetPasswordService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);
  private i18nService = inject(I18nService);
  private utmPreservationService = inject(UtmPreservationService);


  ngOnInit(): void {
    // Fix malformed URL if backend added token incorrectly
    this.fixMalformedUrl();
    
    // Pegar o token da URL
    this.route.queryParams.subscribe(queryParams => {
      this.token = queryParams['token'];
      
      // If token is not found, check if it's in a malformed utm_source param
      if (!this.token || this.token.trim() === '') {
        const utmSource = queryParams['utm_source'];
        if (utmSource) {
          // Check if utm_source contains encoded token (%3Ftoken%3D)
          if (utmSource.includes('%3Ftoken%3D') || utmSource.includes('%3ftoken%3d')) {
            try {
              const decoded = decodeURIComponent(utmSource);
              if (decoded.includes('?token=')) {
                const parts = decoded.split('?token=');
                if (parts.length > 1) {
                  this.token = parts[1];
                }
              }
            } catch (e) {
              console.error('Error decoding utm_source:', e);
            }
          }
          // Check if utm_source contains unencoded token (?token=)
          else if (utmSource.includes('?token=')) {
            const parts = utmSource.split('?token=');
            if (parts.length > 1) {
              this.token = parts[1];
            }
          }
        }
      }
      
      if (!this.token || this.token.trim() === '') {
        this.toastService.showError('Insira o token para continuar', 4000, 'top-center');
        this.redirectToLogin();
        return;
      }
      
      this.validateToken();
    });
  }

  private fixMalformedUrl(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const currentUrl = window.location.href;
    const urlObj = new URL(currentUrl);
    const searchParams = urlObj.searchParams;
    
    // Check if utm_source contains a malformed token (has ?token= encoded)
    const utmSource = searchParams.get('utm_source');
    if (utmSource && utmSource.includes('?token=')) {
      // Extract token from malformed utm_source
      const parts = utmSource.split('?token=');
      const actualUtmSource = parts[0];
      const token = parts[1];
      
      // Fix the URL by setting correct params
      searchParams.set('utm_source', actualUtmSource);
      if (token) {
        searchParams.set('token', token);
      }
      
      // Rebuild URL with correct params
      const fixedUrl = urlObj.pathname + '?' + searchParams.toString();
      
      // Navigate to fixed URL
      this.router.navigateByUrl(fixedUrl, { replaceUrl: true });
      return;
    }
    
    // Also check for encoded malformed URL (%3Ftoken%3D)
    if (utmSource && (utmSource.includes('%3Ftoken%3D') || utmSource.includes('%3ftoken%3d'))) {
      // Decode and extract
      try {
        const decoded = decodeURIComponent(utmSource);
        if (decoded.includes('?token=')) {
          const parts = decoded.split('?token=');
          const actualUtmSource = parts[0];
          const token = parts[1];
          
          searchParams.set('utm_source', actualUtmSource);
          if (token) {
            searchParams.set('token', token);
          }
          
          const fixedUrl = urlObj.pathname + '?' + searchParams.toString();
          this.router.navigateByUrl(fixedUrl, { replaceUrl: true });
          return;
        }
      } catch (e) {
        console.error('Error fixing malformed URL:', e);
      }
    }
    
  }

  private validateToken(): void {
    if (this.isProcessingToken) {
      return;
    }
    
    this.isProcessingToken = true;
    this.loadingToken = true;
    this.resetPasswordService.validateToken(this.token).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          // Token válido, inicializar o formulário
          this.resetForm = this.fb.group({
            password: ['', [Validators.required, this.passwordStrengthValidator]],
            confirmPassword: ['', [Validators.required, this.passwordStrengthValidator]]
          });

          // Listener para validar confirmação de senha
          this.resetForm.valueChanges.subscribe(() => {
            this.passwordMatchValidator(this.resetForm);
          });

          // Listener para requisitos de senha + controle pristine
          this.resetForm.get('password')?.valueChanges.subscribe((password) => {
            this.resetForm.get('confirmPassword')?.updateValueAndValidity();
            this.isPasswordPristine = !password;
            this.updatePasswordRequirements(password || '');
          });

          this.loadingToken = false;
        } else {
          this.loadingToken = false;
          this.toastService.showError(this.i18nService.instant('pages.resetPassword.messages.tokenInvalid'), 4000, 'top-center');
          this.redirectToLogin();
        }
      },
      error: () => {
        this.loadingToken = false;
        this.toastService.showError(this.i18nService.instant('pages.resetPassword.messages.tokenInvalid'), 4000, 'top-center');
        this.redirectToLogin();
      }
    });
  }

  onPasswordToggle(isVisible: boolean): void {
    this.passwordInput.setPasswordVisibility(isVisible);
    this.confirmPasswordInput.setPasswordVisibility(isVisible);
  }

  passwordStrengthValidator(control: AbstractControl) {
    const value = control.value;
    if (!value) {
      return null;
    }
    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const valid = regex.test(value);
    return valid ? null : { invalidPassword: true };
  }

  fullNameValidator(control: AbstractControl) {
    const value = control.value?.trim();
    if (!value) return null;

    const words = value.split(/\s+/).filter((word: string) => word.length > 0);

    if (words.length < 2) {
      return { invalidFullName: true };
    }

    const hasValidWords = words.every((word: string) => word.length >= 2);
    if (!hasValidWords) {
      return { invalidFullName: true };
    }

    return null;
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ mismatch: true });
    } else if (confirmPassword) {
      confirmPassword.setErrors(null);
    }
    return null;
  }

  resetPassword() {
    // ✅ Usar loadingSubmit para envio da senha
    this.loadingSubmit = true;
    this.error = '';

    const request = {
      password: this.resetForm.value.password,
      confirmPassword: this.resetForm.value.confirmPassword
    };

    this.resetPasswordService.resetPassword(this.token, request).subscribe({
      next: (response) => {
        this.loadingSubmit = false;
        if (response.status === 'success') {
          this.toastService.showSuccess(this.i18nService.instant('pages.resetPassword.messages.success'), 4000, 'top-center');
          this.redirectToLogin();
        } else {
          const err = response.status || this.i18nService.instant('pages.resetPassword.messages.error');
          this.error = err;
          this.toastService.showError(err, 5000, 'top-center');
        }
      },
      error: () => {
        this.loadingSubmit = false;
        const err = this.i18nService.instant('pages.resetPassword.messages.genericError');
        this.error = err;
        this.toastService.showError(err, 5000, 'top-center');
      }
    });
  }

  get passwordControl() {
    return this.resetForm.get('password');
  }

  get confirmPasswordControl() {
    return this.resetForm.get('confirmPassword');
  }

  // Atualiza regras da senha em tempo real
  updatePasswordRequirements(password: string): void {
    this.passwordRequirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasSpecialChar: /[@$!%*?&#]/.test(password),
      hasNumber: /\d/.test(password)
    };
  }

  private redirectToLogin(): void {
    // Only add UTM params if they exist
    const queryParams: Record<string, string> = {};
    
    // Get preserved UTM params
    const preservedParams = this.utmPreservationService.preservedUtmParams;
    if (preservedParams && Object.keys(preservedParams).length > 0) {
      Object.entries(preservedParams).forEach(([key, value]) => {
        if (key.toLowerCase().startsWith('utm_')) {
          // Ensure utm_source doesn't contain token
          if (key.toLowerCase() === 'utm_source') {
            // Remove any token that might be embedded in utm_source
            let cleanValue = value;
            if (cleanValue.includes('?token=')) {
              cleanValue = cleanValue.split('?token=')[0];
            }
            if (cleanValue.includes('%3Ftoken%3D') || cleanValue.includes('%3ftoken%3d')) {
              try {
                const decoded = decodeURIComponent(cleanValue);
                if (decoded.includes('?token=')) {
                  cleanValue = decoded.split('?token=')[0];
                }
              } catch (e) {
                // Ignore decode errors
              }
            }
            queryParams[key] = cleanValue;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }
    
    // If no preserved params, check current URL params (but exclude token)
    if (Object.keys(queryParams).length === 0 && typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.forEach((value, key) => {
        if (key.toLowerCase().startsWith('utm_') && key.toLowerCase() !== 'token') {
          // Clean utm_source if it contains token
          if (key.toLowerCase() === 'utm_source') {
            let cleanValue = value;
            if (cleanValue.includes('?token=')) {
              cleanValue = cleanValue.split('?token=')[0];
            }
            if (cleanValue.includes('%3Ftoken%3D') || cleanValue.includes('%3ftoken%3d')) {
              try {
                const decoded = decodeURIComponent(cleanValue);
                if (decoded.includes('?token=')) {
                  cleanValue = decoded.split('?token=')[0];
                }
              } catch (e) {
                // Ignore decode errors
              }
            }
            queryParams[key] = cleanValue;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }
    
    // Only navigate with query params if there are UTM params
    if (Object.keys(queryParams).length > 0) {
      this.router.navigate(['/login'], { queryParams });
    } else {
      this.router.navigate(['/login']);
    }
  }
}